import { AnimatePresence, motion } from 'framer-motion';
import moment from 'moment';
import { useState } from 'react';
import { OutlineChevronDownIcon, SolidCurrencyDollarIcon } from '@tempmee/react-icons';
import { colors } from '@tempmee/tailwind';
import { Center, Column, Row, Typography } from '@tempmee/react';
import { IJob } from '~/models';
import { getProfessionalPayChargeDate } from '~/utils/chargeTimeline';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

interface IChargeTimelineCollapsibleBannerProps {
  job?: Partial<IJob> | null;
}

export const ChargeTimelineCollapsibleBanner = ({
  job,
}: IChargeTimelineCollapsibleBannerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const chargeTimelineDays = RemoteConfigUtils.getChargeTimelineDays();
  const chargedHours = chargeTimelineDays * 24;

  if (!job?.job_datetime) {
    return null;
  }

  const { offer_owner: offerOwner, job_datetime: jobDateTime } = job;

  // Check if office is pre-approved (invoiced)
  const isPreApproved = offerOwner?.is_pre_approved;

  // Calculate hours until shift start
  const hoursUntilShift = moment(jobDateTime).diff(moment(), 'hours');

  // Only show if NOT pre-approved AND more than 168 hours (7 days) until shift
  if (isPreApproved || hoursUntilShift <= chargedHours) {
    return null;
  }

  // Charge date (shift start minus the configured charge-timeline window)
  const chargeDate = moment(getProfessionalPayChargeDate(jobDateTime)).format('ddd, MMM DD, YYYY');

  return (
    <Column style={{ margin: '20px 0px' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          all: 'unset',
          width: '100%',
          backgroundColor: colors.success[30],
          border: `1px solid ${colors.success[600]}`,
          padding: '10px 12px',
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          boxSizing: 'border-box',
        }}
      >
        {/* Top row: Icon + Title + Badge + Arrow */}
        <Row align="center" justify="between">
          <Row align="center">
            <SolidCurrencyDollarIcon size={24} color={colors.success[500]} />
            <Typography as="span" variant="body1" bold color={colors.success[500]} style={{ marginLeft: 4 }}>
              Book Now, Pay Later!
            </Typography>
            <Center
              style={{
                marginLeft: 10,
                height: 20,
                width: 48,
                backgroundColor: colors.secondary[500],
                borderRadius: 3,
              }}
            >
              <span
                style={{
                  fontFamily: 'Nunito',
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.shades[0],
                  textTransform: 'uppercase',
                }}
              >
                NEW
              </span>
            </Center>
          </Row>
          <Center
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s ease',
            }}
          >
            <OutlineChevronDownIcon size={24} color={colors.success[500]} />
          </Center>
        </Row>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            style={{
              overflow: 'hidden',
              backgroundColor: colors.success[30],
              border: `1px solid ${colors.success[600]}`,
              borderTopWidth: 0,
              padding: '0px 40px 12px 40px',
              marginTop: -1,
            }}
          >
            <Typography as="span" color={colors.success[500]}>Professional Pay: Charged on {chargeDate}</Typography>
            <br />
            <Typography as="span" color={colors.success[500]}>Additional Fees: Charged upon confirmation</Typography>
          </motion.div>
        )}
      </AnimatePresence>
    </Column>
  );
};
